import type { Dispatch, SetStateAction } from 'react';
import type { AppState, Estate, Plan } from '../types';
import { id, initialGuestState } from './domain';
import { reconcileState, sameCustomerContent } from './reconcile';
import { bindWorkspaceState, setCloudIdentity } from './cloud';
import {
  loadState,
  saveState,
  validateState,
  hasUnsyncedState,
  acknowledgeStoredState,
  preserveRecoveryState,
  loadCloudBaseline,
  loadGuestState,
  saveGuestState,
  clearGuestState,
  hasGuestPlan,
  type StorageErrorKind,
} from './storage';

export interface WorkspaceUser {
  id: string;
  email: string;
}
export interface WorkspaceSnapshot {
  state: AppState;
  user: WorkspaceUser | null;
  generation: number;
  revision: number;
  cloudDirty: boolean;
  recoveredDirty: boolean;
  cloudConflict: { local: AppState; remote: AppState; backupError?: string } | null;
  authReady: boolean;
  saveStatus: 'saved' | 'saving' | 'error';
  storageError: string | null;
}

// Synchronous identity transitions keep delayed callbacks out of another account's cache.
export function isGuestCustomField(f: { id: string; estateId?: string }): boolean {
  return (
    typeof f.id === 'string' &&
    !f.id.startsWith('example-') &&
    (!f.estateId || !String(f.estateId).startsWith('example-'))
  );
}

function isAppState(obj: unknown): obj is AppState {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'estates' in obj &&
    Array.isArray((obj as AppState).estates)
  );
}

export function transferGuestPlanToAccount(
  first: WorkspaceUser | AppState,
  second?: AppState,
): AppState {
  let userState: AppState;
  let guest: AppState | undefined;

  if (isAppState(first)) {
    if (
      second &&
      second.fields &&
      second.fields.some(isGuestCustomField) &&
      (!first.fields || !first.fields.some(isGuestCustomField))
    ) {
      guest = second;
      userState = first;
    } else {
      guest = first;
      userState = second || loadState().state;
    }
  } else {
    const userLoaded = loadState(first.id);
    userState = userLoaded.state;
    guest = second;
  }

  if (!guest || !guest.fields || !guest.fields.some(isGuestCustomField)) {
    const loadedGuest = loadGuestState();
    if (loadedGuest.state.fields.some(isGuestCustomField)) {
      guest = loadedGuest.state;
    }
  }

  if (!guest || !guest.fields) {
    return userState;
  }

  const guestFields = guest.fields.filter(isGuestCustomField);
  if (guestFields.length === 0) {
    return userState;
  }

  userState = structuredClone(userState);

  const guestEstate =
    guest.estates.find((e) => e.id === guest.selectedEstateId) || guest.estates[0];
  const guestPlan = guest.plans.find((p) => p.id === guest.selectedPlanId) || guest.plans[0];

  const isNewOrEmpty =
    userState.estates.length === 0 ||
    (userState.fields.length === 0 &&
      userState.estates.length === 1 &&
      userState.estates[0].name === 'Mit Revir');

  if (isNewOrEmpty) {
    const targetEstateId = userState.estates[0]?.id || id();
    const targetPlanId = userState.plans[0]?.id || id();
    const estateName =
      guestEstate?.name && guestEstate.name !== 'Mit Revir' ? guestEstate.name : 'Mit Revir';

    userState.estates = [
      {
        id: targetEstateId,
        name: estateName,
        address: guestEstate?.address || '',
        center: guestEstate?.center || [11.0, 56.0],
        createdAt: new Date().toISOString(),
      },
    ];
    userState.fields = guestFields.map((f) => ({ ...f, estateId: targetEstateId }));
    userState.plans = [
      {
        id: targetPlanId,
        estateId: targetEstateId,
        name: guestPlan?.name || 'Vildtager ' + new Date().getFullYear(),
        year: guestPlan?.year || new Date().getFullYear(),
        status: 'draft',
        notes: guestPlan?.notes || '',
        selections: { ...(guestPlan?.selections || {}) },
        updatedAt: new Date().toISOString(),
      },
    ];
    userState.selectedEstateId = targetEstateId;
    userState.selectedPlanId = targetPlanId;
  } else {
    let baseName = guestEstate?.name && guestEstate.name !== '' ? guestEstate.name : 'Mit Revir';
    let estateName = baseName;
    let counter = 2;
    while (userState.estates.some((e) => e.name.toLowerCase() === estateName.toLowerCase())) {
      estateName = `${baseName} ${counter++}`;
    }

    const newEstateId = id();
    const newPlanId = id();
    const newEstate: Estate = {
      id: newEstateId,
      name: estateName,
      address: guestEstate?.address || '',
      center: guestEstate?.center || [11.0, 56.0],
      createdAt: new Date().toISOString(),
    };

    const fieldIdMap = new Map<string, string>();
    const migratedFields = guestFields.map((f) => {
      const newFieldId = id();
      fieldIdMap.set(f.id, newFieldId);
      return {
        ...f,
        id: newFieldId,
        estateId: newEstateId,
      };
    });

    const migratedSelections: Record<string, string> = {};
    if (guestPlan?.selections) {
      for (const [oldFid, prodId] of Object.entries(guestPlan.selections)) {
        const mapped = fieldIdMap.get(oldFid);
        if (mapped) migratedSelections[mapped] = prodId;
      }
    }

    const newPlan: Plan = {
      id: newPlanId,
      estateId: newEstateId,
      name: guestPlan?.name || 'Vildtager ' + new Date().getFullYear(),
      year: guestPlan?.year || new Date().getFullYear(),
      status: 'draft',
      notes: guestPlan?.notes || '',
      selections: migratedSelections,
      updatedAt: new Date().toISOString(),
    };

    userState.estates.push(newEstate);
    userState.fields.push(...migratedFields);
    userState.plans.push(newPlan);
    userState.selectedEstateId = newEstateId;
    userState.selectedPlanId = newPlanId;
  }

  const userId = 'id' in first && !('estates' in first) ? first.id : undefined;
  if (userId) {
    saveState(userState, userId);
  }
  clearGuestState();

  return userState;
}

export class WorkspaceStore {
  private current: WorkspaceSnapshot;
  private listeners = new Set<() => void>();
  private recovery = new Map<string, { state: AppState; error: string; kind: StorageErrorKind }>();
  private failureKind: StorageErrorKind | null;

  private readonly temporary: boolean;

  constructor(configured: boolean, temporaryState?: AppState) {
    this.temporary = temporaryState !== undefined;
    const loaded = this.temporary
      ? { state: structuredClone(temporaryState!), error: null }
      : loadGuestState();
    this.failureKind = loaded.error ? 'corrupt' : null;
    if (!this.temporary) bindWorkspaceState(loaded.state, null);
    this.current = {
      state: loaded.state,
      user: null,
      generation: 0,
      revision: 0,
      cloudDirty: false,
      recoveredDirty: false,
      cloudConflict: null,
      authReady: this.temporary || !configured,
      saveStatus: loaded.error ? 'error' : this.temporary ? 'saved' : 'saving',
      storageError: loaded.error,
    };
  }

  getSnapshot = (): WorkspaceSnapshot => this.current;
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
  private publish(next: WorkspaceSnapshot): void {
    this.current = next;
    for (const listener of this.listeners) listener();
  }

  switchUser(user: WorkspaceUser | null): void {
    if (this.temporary) return;
    if (this.current.authReady && this.current.user?.id === user?.id) {
      if (this.current.user?.email !== user?.email) this.publish({ ...this.current, user });
      return;
    }
    const previous = this.current;
    if (previous.authReady && previous.saveStatus === 'saving' && !previous.storageError)
      this.persist(previous.generation);
    if (previous.authReady && this.current.storageError)
      this.recovery.set(previous.user?.id ?? '', {
        state: this.current.state,
        error: this.current.storageError,
        kind: this.failureKind ?? 'unavailable',
      });
    const restored = this.recovery.get(user?.id ?? '');

    const hadGuestData =
      (previous.user === null && previous.state.fields.some(isGuestCustomField)) ||
      (typeof localStorage !== 'undefined' && hasGuestPlan());
    let loaded = restored ?? (user ? loadState(user.id) : loadGuestState());
    let transferred = false;
    if (user && hadGuestData) {
      const guestState =
        previous.user === null && previous.state.fields.some(isGuestCustomField)
          ? previous.state
          : loadGuestState().state;
      const migratedState = transferGuestPlanToAccount(guestState, loaded.state);
      loaded = { state: migratedState, error: null };
      transferred = true;
      saveState(migratedState, user.id);
      clearGuestState();
    }

    this.failureKind = restored?.kind ?? (loaded.error ? 'corrupt' : null);
    const state = user
      ? { ...loaded.state, profile: { ...loaded.state.profile, email: user.email } }
      : loaded.state;
    bindWorkspaceState(state, user?.id ?? null);
    setCloudIdentity(user?.id ?? null);
    this.publish({
      state,
      user,
      generation: previous.generation + 1,
      revision: 0,
      cloudDirty: Boolean(user && (transferred || restored || hasUnsyncedState(user.id))),
      recoveredDirty: Boolean(user && (transferred || restored || hasUnsyncedState(user.id))),
      cloudConflict: null,
      authReady: true,
      saveStatus: loaded.error ? 'error' : 'saving',
      storageError: loaded.error,
    });
  }

  update(action: SetStateAction<AppState>, generation: number): void {
    if (generation !== this.current.generation || !this.current.authReady) return;
    const state = typeof action === 'function' ? action(this.current.state) : action;
    if (!this.temporary) bindWorkspaceState(state, this.current.user?.id ?? null);
    const corrected = this.failureKind === 'validation' && validateState(state);
    if (corrected) {
      this.failureKind = null;
      this.recovery.delete(this.current.user?.id ?? '');
    }
    const storageError = corrected ? null : this.current.storageError;
    this.publish({
      ...this.current,
      state,
      revision: this.current.revision + 1,
      cloudDirty: Boolean(
        this.current.user &&
        (this.current.cloudDirty || !sameCustomerContent(this.current.state, state)),
      ),
      storageError,
      saveStatus: storageError ? 'error' : this.temporary ? 'saved' : 'saving',
    });
    // Persist synchronously: closing/reloading before the debounce must not lose edits.
    this.persist(generation);
  }

  setter(generation = this.current.generation): Dispatch<SetStateAction<AppState>> {
    return (action) => this.update(action, generation);
  }

  hydrateCloud(state: AppState, generation: number, revision: number): boolean {
    if (this.temporary) return false;
    if (
      generation !== this.current.generation ||
      revision !== this.current.revision ||
      this.current.cloudDirty ||
      this.current.storageError ||
      !this.current.authReady
    )
      return false;
    if (!this.temporary) bindWorkspaceState(state, this.current.user?.id ?? null);
    this.publish({ ...this.current, state, revision: revision + 1, saveStatus: 'saving' });
    this.persist(generation);
    if (this.current.user && !this.current.storageError)
      acknowledgeStoredState(state, this.current.user.id);
    return true;
  }

  // Only call after explicit confirmation to discard local edits. Backup remains
  // account-scoped and a fetch that raced with new edits is still rejected.
  recoverCloud(state: AppState, generation: number, revision: number): boolean {
    if (
      generation !== this.current.generation ||
      revision !== this.current.revision ||
      !this.current.user ||
      this.current.storageError ||
      !validateState(state)
    )
      return false;
    if (!preserveRecoveryState(this.current.state, this.current.user.id)) return false;
    bindWorkspaceState(state, this.current.user.id);
    this.publish({
      ...this.current,
      state,
      revision: revision + 1,
      cloudDirty: false,
      recoveredDirty: false,
      cloudConflict: null,
      saveStatus: 'saving',
    });
    this.persist(generation);
    if (this.current.storageError || !acknowledgeStoredState(state, this.current.user.id)) {
      this.publish({ ...this.current, cloudDirty: true, recoveredDirty: true });
      return false;
    }
    return true;
  }

  reconcileCloud(remote: AppState, generation: number): boolean {
    if (generation !== this.current.generation || !this.current.user || this.current.storageError)
      return false;
    const scope = this.current.user.id;
    const local = this.current.state;
    const baseline = loadCloudBaseline(scope);
    const merged = !this.current.cloudDirty ? remote : reconcileState(baseline, local, remote);
    const needsRecovery = this.current.cloudDirty || !merged || !validateState(merged);
    if (needsRecovery) {
      const localSaved = preserveRecoveryState(local, scope);
      const remoteSaved = preserveRecoveryState(remote, scope);
      if (!localSaved || !remoteSaved) {
        const backupError =
          'Browseren kunne ikke gemme begge sikkerhedskopier. Dine lokale data er stadig åbne, og serverdata er uændrede. Download begge versioner før du lukker eller genindlæser siden.';
        this.publish({ ...this.current, cloudConflict: { local, remote, backupError } });
        throw new Error(backupError);
      }
    }
    if (!merged || !validateState(merged)) {
      this.publish({ ...this.current, cloudConflict: { local, remote } });
      return false;
    }
    if (!acknowledgeStoredState(remote, scope))
      throw new Error(
        'Browseren kunne ikke gemme kontoens versionskvittering. Ingen lokale data er erstattet; kontrollér browserens lagerplads.',
      );
    bindWorkspaceState(merged, scope);
    const dirty = !sameCustomerContent(merged, remote);
    this.publish({
      ...this.current,
      state: merged,
      revision: this.current.revision + 1,
      cloudDirty: dirty,
      recoveredDirty: false,
      cloudConflict: null,
      saveStatus: 'saving',
    });
    this.persist(generation);
    return !this.current.storageError;
  }

  acknowledgeCloud(state: AppState, generation: number): boolean {
    if (generation !== this.current.generation || !this.current.user || this.current.storageError)
      return false;
    // A successful older save is still the baseline for edits made while it was in flight.
    if (!acknowledgeStoredState(state, this.current.user.id))
      throw new Error(
        'Ændringerne er sendt, men browseren kunne ikke gemme kvitteringen. Lokale data er bevaret; kontrollér browserens lagerplads.',
      );
    const dirty = !sameCustomerContent(state, this.current.state);
    this.publish({
      ...this.current,
      cloudDirty: dirty,
      recoveredDirty: false,
      cloudConflict: null,
    });
    return !dirty;
  }

  persist(generation: number): void {
    if (this.temporary) return;
    if (
      generation !== this.current.generation ||
      !this.current.authReady ||
      this.current.storageError
    )
      return;
    const result = saveState(this.current.state, this.current.user?.id);
    this.failureKind = result.ok ? null : result.kind;
    if (!result.ok && this.current.user)
      preserveRecoveryState(this.current.state, this.current.user.id);
    if (!result.ok)
      this.recovery.set(this.current.user?.id ?? '', {
        state: this.current.state,
        error: result.error,
        kind: result.kind,
      });
    else this.recovery.delete(this.current.user?.id ?? '');
    if (!this.current.user) {
      saveGuestState(this.current.state);
    }
    this.publish({
      ...this.current,
      saveStatus: result.ok ? 'saved' : 'error',
      storageError: result.error,
    });
  }

  authFailure(message: string): void {
    if (this.current.authReady) return;
    this.publish({ ...this.current, saveStatus: 'error', storageError: message });
  }
}
